import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Overline, SectionTitle, SportBadge, ClassBadge, EmptyState } from "@/components/portal/Bits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, ChevronRight, Users2, Plus, Loader2 } from "lucide-react";

function Avatar({ athlete }) {
  const initials = (athlete.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");
  if (athlete.headshot_url) {
    return <img src={athlete.headshot_url} alt={athlete.name} className="h-12 w-12 rounded-md object-cover object-top border border-border" />;
  }
  return (
    <div className="h-12 w-12 rounded-md bg-secondary border border-border flex items-center justify-center font-display text-lg text-muted-foreground">
      {initials}
    </div>
  );
}

const ALL = "__all__";

export default function Roster() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [athletes, setAthletes] = useState([]);
  const [options, setOptions] = useState({ sports: [], schools: [], classifications: [], positions: [] });
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ sport: ALL, school: ALL, classification: ALL, position: ALL });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/athletes/filters/options");
      setOptions(data);
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== ALL) params[k] = v; });
    const { data } = await api.get("/athletes", { params });
    setAthletes(data);
    setLoading(false);
  }, [q, filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setF = (k) => (v) => setFilters((prev) => ({ ...prev, [k]: v }));

  return (
    <div>
      <SectionTitle
        sub="Search and filter Merch Run's confirmed client roster."
        right={isAdmin && <AddAthleteDialog onCreated={(a) => navigate(`/athlete/${a.id}`)} />}
      >
        Roster
      </SectionTitle>

      <Panel className="p-4 mb-6">
        <div className="grid gap-3 md:grid-cols-5">
          <div className="relative md:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name" className="pl-9 bg-secondary border-border" data-testid="roster-search-input" />
          </div>
          {[
            ["sport", "Sport", options.sports],
            ["school", "School", options.schools],
            ["classification", "Class", options.classifications],
            ["position", "Position", options.positions],
          ].map(([key, label, opts]) => (
            <Select key={key} value={filters[key]} onValueChange={setF(key)}>
              <SelectTrigger className="bg-secondary border-border" data-testid={`filter-${key}`}>
                <SelectValue placeholder={label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All {label.toLowerCase()}s</SelectItem>
                {(opts || []).map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
        </div>
      </Panel>

      {loading ? (
        <div className="text-muted-foreground">Loading roster…</div>
      ) : athletes.length === 0 ? (
        <EmptyState icon={Users2} title="No athletes match" hint="Try clearing filters or a different search." testId="roster-empty" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="roster-grid">
          {athletes.map((a) => (
            <button
              key={a.id}
              onClick={() => navigate(`/athlete/${a.id}`)}
              data-testid={`roster-card-${a.id}`}
              className="group text-left"
            >
              <Panel className="p-5 transition-all hover:-translate-y-1 hover:border-white/30">
                <div className="flex items-center gap-4">
                  <Avatar athlete={a} />
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-xl text-white truncate">{a.name}</div>
                    <div className="text-sm text-muted-foreground truncate">{a.school}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <SportBadge sport={a.sport} />
                  <ClassBadge classification={a.classification} />
                  {a.position && <span className="text-xs text-muted-foreground">{a.position}</span>}
                </div>
              </Panel>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


const SPORTS = [["football", "Football"], ["basketball", "Basketball"]];
const CLASSES = ["College", "High School / Prep"];

function AddAthleteDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { name: "", sport: "football", school: "", classification: "", position: "", height: "", weight: "" };
  const [form, setForm] = useState(empty);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim() || !form.school.trim()) {
      toast.error("Name and school are required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(), sport: form.sport, school: form.school.trim(),
        classification: form.classification || null,
        position: form.position.trim() || null,
        height: form.height.trim() || null,
        weight: form.weight.trim() || null,
      };
      const { data } = await api.post("/athletes", body);
      toast.success(`${data.name} added to the roster`);
      setOpen(false);
      setForm(empty);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm(empty); }}>
      <DialogTrigger asChild>
        <Button className="bg-white text-black hover:bg-white/90 font-semibold gap-2" data-testid="add-athlete-button">
          <Plus className="h-4 w-4" /> Add athlete
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add athlete</DialogTitle>
          <DialogDescription>
            Create a new roster profile. Only enter confirmed values — leave the rest blank to keep
            them “Not verified”. Recruiting research can be added on the athlete’s profile.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input value={form.name} onChange={set("name")} className="bg-secondary border-border" data-testid="add-athlete-name" />
          </div>
          <div className="space-y-2">
            <Label>Sport *</Label>
            <Select value={form.sport} onValueChange={(v) => setForm((p) => ({ ...p, sport: v }))}>
              <SelectTrigger className="bg-secondary border-border" data-testid="add-athlete-sport"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SPORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>School *</Label>
            <Input value={form.school} onChange={set("school")} className="bg-secondary border-border" data-testid="add-athlete-school" />
          </div>
          <div className="space-y-2">
            <Label>Classification</Label>
            <Select value={form.classification || undefined} onValueChange={(v) => setForm((p) => ({ ...p, classification: v }))}>
              <SelectTrigger className="bg-secondary border-border" data-testid="add-athlete-classification"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Position</Label>
            <Input value={form.position} onChange={set("position")} className="bg-secondary border-border" data-testid="add-athlete-position" />
          </div>
          <div className="space-y-2">
            <Label>Height</Label>
            <Input value={form.height} onChange={set("height")} placeholder={`e.g. 6'1"`} className="bg-secondary border-border" data-testid="add-athlete-height" />
          </div>
          <div className="space-y-2">
            <Label>Weight</Label>
            <Input value={form.weight} onChange={set("weight")} placeholder="e.g. 187" className="bg-secondary border-border" data-testid="add-athlete-weight" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-white text-black hover:bg-white/90 font-semibold" data-testid="add-athlete-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add athlete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
